import { useEffect, useState } from "react";
import { ChartScale, MarkerData } from "..";
import _ from "lodash";
import { ChartType } from "..";
import { useAppSelector } from "../../../../app/hooks";
import { useParams } from "react-router";
import { Device, selectDevices } from "../../../../features/devices/deviceSlice";
import {
  Dashboards,
  selectDashboards,
} from "../../../../features/dashboards/dashboardSlice";
import { MdAddLocation } from "react-icons/md";
import { EditChart, EditChartProps } from "../EditChart";
import { MarkerBox } from "./NewMarkerMenu";
import { FormInput } from "../../../FormInput";
import NotificationMarkerEdit, {
  MapType,
  getNewNotificationMarkerData,
} from "./NotificationMarker/NotificationMarkerEdit";
import { StatusMarkerButtonIcon } from "./NotificationMarker/utils";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/ChartsText";

type Props = {
  id: string;
  data: ChartType;
  remove: (id: string) => void;
  updateChart: (data: ChartType) => void;
  setShowChartEdit: (isOpen: boolean) => void;
};

function EditMap({ id, remove, updateChart, data, setShowChartEdit }: Props) {
  type FormInputData = {
    optionValue: string;
    optionName: string;
  };

  const language: LanguageText = useGetLanguage({
    fileName: "ChartsText",
  });

  const [markerList, setMarkerList] = useState<MarkerData[] | any[]>(
    data.chartData ?? []
  );

  const [title, setTitle] = useState("");
  const [userDeviceData, setUserDeviceData] = useState<FormInputData[]>([]);
  const [loading, setLoading] = useState(false);

  let { dashboardid } = useParams();
  let { devices } = useAppSelector(selectDevices);
  let { dashboards } = useAppSelector(selectDashboards);

  function populateDeviceSelect() {
    var tempUserDeviceData: FormInputData[] = [
      {
        optionValue: "new",
        optionName: language?.charts?.gauge?.inputDefaults?.selectDevice,
      },
    ];
    const dashboardDevices: Device[] = _.filter(devices, (device: Device) => {
      return device.group_id === dashboards[dashboardid as keyof Dashboards].group_id;
    });
    _.map(dashboardDevices, (device: Device) => {
      if (device.eui !== null && device.name !== null) {
        tempUserDeviceData.push({
          optionValue: device.eui,
          optionName: device.name,
        });
      }
    });
    setUserDeviceData(tempUserDeviceData);
  }

  function getTitleAndMarkerListFromParams() {
    setTitle(data.title.en);
    setMarkerList(data.chartData ?? []);
  }

  useEffect(() => {
    populateDeviceSelect();
  }, [devices, dashboardid, dashboards]);

  useEffect(() => {
    getTitleAndMarkerListFromParams();
  }, [data]);

  const makeSelected = (index: number) => {
    let newMarkerList = [...markerList];
    newMarkerList.slice(0, newMarkerList.length).map((itemT, indexT) => {
      newMarkerList[indexT] = { ...itemT, selected: false };
      if (index === indexT) {
        newMarkerList[indexT] = { ...itemT, selected: true };
      }
    });
    setMarkerList(newMarkerList);
    saveAndCloseChartSettings(newMarkerList);
  };

  const toggleShowUnit = (index: number) => {
    let newMarkerList = [...markerList];
    newMarkerList.slice(0, newMarkerList.length).map((itemT, indexT) => {
      if (index === indexT) {
        newMarkerList[indexT] = { ...itemT, showUnit: !itemT.showUnit };
      }
    });
    setMarkerList(newMarkerList);
  };

  const updateMarker = (
    {
      deviceID,
      elemID,
      lng,
      lat,
      label,
      phenomena,
      colour,
      draggable,
      selected,
      markerType,
      notificationID,
      activeStatusImageId,
      inactiveStatusImageId,
      activeAcknowledgedImageId,
      markerSize,
    }: MarkerData,
    index: number
  ) => {
    let newMarkerList = [...markerList];
    newMarkerList.slice(0, newMarkerList.length).map((itemT, indexT) => {
      newMarkerList[indexT] = { ...itemT, selected: false };
      if (index === indexT) {
        if (itemT.markerType === "notification") {
          newMarkerList[indexT] = {
            ...itemT,
            notificationID,
            lat,
            lng,
            activeStatusImageId,
            inactiveStatusImageId,
            activeAcknowledgedImageId,
            markerSize,
          };
        } else {
          newMarkerList[indexT] = {
            ...itemT,
            deviceID,
            elemID,
            lng,
            lat,
            label,
            phenomena,
            colour,
            draggable,
            selected,
          };
        }
      }
    });
    setMarkerList(newMarkerList);
  };

  const addNewMarker = ({
    deviceID,
    elemID,
    lng,
    lat,
    label,
    phenomena,
    colour,
    draggable,
    selected,
  }: MarkerData) => {
    setMarkerList(
      _.concat(markerList, {
        deviceID,
        elemID,
        lng,
        lat,
        label,
        phenomena,
        colour,
        draggable,
        selected,
      })
    );
  };

  const addNotificationMarker = () => {
    const newMarker = getNewNotificationMarkerData();
    setMarkerList(_.concat(markerList, { ...newMarker }));
  };

  function setCenter() {
    let newData = {
      ...data,
      chartScale: {
        ...data.chartScale,
        y_axis: {
          ...(data?.chartScale?.y_axis ?? { max_auto: false, min: 0, max: 0 }),
          min_auto: true,
        },
      },
    };

    saveAndCloseChartSettings(newData.chartData, newData.chartScale);
  }

  function removeMarker(index: number) {
    setMarkerList(markerList.filter((_, i) => i !== index));
  }

  const itemList = markerList.slice(0, markerList.length).map((item, index) => {
    if (markerList.length > 0) {
      if (item.markerType === "notification") {
        return (
          <>
            <hr />
            <NotificationMarkerEdit
              mapType={MapType.LAND}
              dashboardID={dashboardid ?? ""}
              marker={item}
              index={index}
              updateMarker={updateMarker}
              removeMarker={removeMarker}
            />
            <hr />
          </>
        );
      } else
        return (
          <MarkerBox
            marker={item}
            index={index}
            userDeviceData={userDeviceData}
            showCoords={true}
            mapType="land"
            updateMarker={updateMarker}
            removeMarker={removeMarker}
            toggleShowUnit={toggleShowUnit}
            makeSelected={makeSelected}
          />
        );
    }
  });

  const settingsBox = (
    <div className="p-2 my-1 space-y-1 flex w-full border-4 bg-gray-200 scrollbar-none rounded-lg flex-col border-gray-200">
      <FormInput
        htmlFor="name"
        label={"Title"}
        value={title}
        onChange={(event) => {
          setTitle(event.target.value);
        }}
      />
    </div>
  );

  const newButtons = [
    {
      text: language?.charts?.map?.markers?.value?.buttons?.addNew,
      onClick: () =>
        addNewMarker({
          deviceID: "new",
          elemID: "0",
          lat: 0,
          lng: 0,
          phenomena: "new",
          colour:
            "#" +
            Math.floor(Math.random() * 16777215)
              .toString(16)
              .padStart(6, "0")
              .toUpperCase(),
          draggable: false,
          selected: false,
        }),
      icon: (
        <MdAddLocation
          size={40}
          className="bg-white opacity-0 group-hover:opacity-70 absolute -ml-[6px] -mt-5 h-16 w-[calc(100%+12px)]"
        />
      ),
    },
    {
      text: language?.charts?.map?.markers?.notification?.buttons?.addNew,
      onClick: () => addNotificationMarker(),
      icon: StatusMarkerButtonIcon,
    },
  ];

  function onDelete() {
    remove(id);
    setShowChartEdit(false);
  }

  function saveChartSettings(
    chartData: MarkerData[],
    chartScale: ChartScale | undefined
  ) {
    updateChart({
      i: id,
      type: "map",
      title: { en: title },
      chartData: chartData
        ? chartData
        : _.filter(markerList, (item) => item.deviceID !== "new"),
      chartScale: chartScale ? chartScale : data.chartScale,
    });
  }

  function saveAndCloseChartSettings(chartData?: MarkerData[], chartScale?: ChartScale) {
    updateChart({
      i: id,
      type: "map",
      title: { en: title },
      chartData: chartData
        ? chartData
        : _.filter(markerList, (item) => {
            if (item.markerType === "notification") {
              return item.notificationID !== "";
            } else return item.deviceID !== "new";
          }),

      chartScale: chartScale ? chartScale : data.chartScale,
    });
    if (chartData) {
      setShowChartEdit(false);
    } else {
      setLoading(true);
      setTimeout(function () {
        setShowChartEdit(false);
        setLoading(false);
      }, 300);
    }
  }

  const EditChartObjects: EditChartProps = {
    editMapType: language?.charts?.map?.landMap?.editMap,
    settingsBox: settingsBox,
    itemList: itemList,
    newButtons: newButtons,
    onDelete: onDelete,
    onSave: saveAndCloseChartSettings,
    loading: loading,
  };

  return <EditChart {...EditChartObjects} />;
}

export default EditMap;
